import { LEARNING_RATE, type CategoryKey, type Confidence } from "../categories/generated.js";
import type { Item } from "../types/entities.js";

/**
 * Die Reihenfolge, in der in einem Einkauf tatsächlich abgehakt wurde.
 * Jede Kategorie zählt einmal, beim ersten Abhaken. Wer am Ende noch einmal
 * zur Molkerei zurückläuft, verschiebt sie damit nicht ans Ende.
 */
export function observedCategoryOrder(items: readonly Item[]): CategoryKey[] {
  const checked = items
    .filter((item) => item.is_checked && item.checked_at !== null && item.category !== null)
    .sort((a, b) => Date.parse(a.checked_at ?? "") - Date.parse(b.checked_at ?? ""));

  const seen = new Set<CategoryKey>();
  const order: CategoryKey[] = [];
  for (const item of checked) {
    const category = item.category;
    if (category === null || seen.has(category)) continue;
    seen.add(category);
    order.push(category);
  }
  return order;
}

/**
 * Rechnet die beobachtete Reihenfolge in die gespeicherte ein.
 *
 * Verfahren in drei Schritten:
 *
 * 1. Kategorien, die der Laden noch nicht kennt, kommen hinten dazu.
 * 2. Das Ziel entsteht, indem die beobachteten Kategorien ihre Plätze
 *    untereinander tauschen. Kategorien, die im Einkauf nicht vorkamen,
 *    behalten ihren Platz, über sie sagt der Einkauf ja nichts.
 * 3. Von der aktuellen Folge wird ein Anteil der Widersprüche zum Ziel
 *    aufgelöst, nicht alle. Die Lernrate ist genau dieser Anteil.
 *
 * Warum nicht einfach Ränge mischen: dabei entstehen Fixpunkte. Bewegen sich
 * zwei Kategorien aufeinander zu, heben sich die Bewegungen auf, und die
 * Reihenfolge ändert sich nie mehr, egal wie oft man einkauft. Das Auflösen
 * von Widersprüchen hat dieses Problem nicht: gleichbleibende Beobachtung
 * führt über mehrere Einkäufe verlässlich zum Ziel.
 */
export function learnCategoryOrder(
  current: readonly CategoryKey[],
  observed: readonly CategoryKey[],
  rate: number,
): CategoryKey[] {
  if (observed.length < 2 || rate <= 0) return [...current];

  const weight = Math.min(Math.max(rate, 0), 1);

  const known = [...current];
  for (const category of observed) {
    if (!known.includes(category)) known.push(category);
  }

  const target = targetOrder(known, observed);

  const rank = new Map(target.map((category, index) => [category, index]));
  const result = [...known];
  const ranks = () => result.map((category) => rank.get(category) ?? 0);

  let remaining = Math.max(1, Math.round(countInversions(ranks()) * weight));
  while (remaining > 0) {
    const index = firstInversion(ranks());
    if (index < 0) break;
    const left = result[index];
    const right = result[index + 1];
    if (left === undefined || right === undefined) break;
    result[index] = right;
    result[index + 1] = left;
    remaining -= 1;
  }

  return result;
}

/**
 * Das Ziel: die beobachtete Folge ist das Gerüst, die übrigen Kategorien
 * behalten ihre Verschachtelung. Eine Kategorie, vor der bisher zwei
 * beobachtete Kategorien lagen, liegt auch im Ziel hinter zwei von ihnen.
 *
 * Das Ziel hängt nur von der Beobachtung ab, nicht vom Fortschritt des
 * Lernens. Nur deshalb wandert die Folge über mehrere Einkäufe verlässlich
 * dorthin, statt auf halbem Weg zu verharren.
 */
function targetOrder(known: readonly CategoryKey[], observed: readonly CategoryKey[]): CategoryKey[] {
  const backbone = observed.filter((category) => known.includes(category));
  const result: CategoryKey[] = [...backbone];

  let seenObserved = 0;
  for (const category of known) {
    if (backbone.includes(category)) {
      seenObserved += 1;
      continue;
    }
    // Hinter derselben Zahl beobachteter Kategorien einsortieren wie bisher.
    const at = Math.min(seenObserved + countInsertedBefore(result, backbone, seenObserved), result.length);
    result.splice(at, 0, category);
  }

  return result;
}

/** Wie viele nicht beobachtete Kategorien schon vor dieser Stelle liegen. */
function countInsertedBefore(result: readonly CategoryKey[], backbone: readonly CategoryKey[], seenObserved: number): number {
  let observedSoFar = 0;
  let inserted = 0;
  for (const category of result) {
    if (backbone.includes(category)) {
      observedSoFar += 1;
      if (observedSoFar > seenObserved) break;
    } else if (observedSoFar <= seenObserved) {
      inserted += 1;
    }
  }
  return inserted;
}

/** Wie weit die Folge vom Ziel entfernt ist: Zahl der verdrehten Paare. */
function countInversions(values: readonly number[]): number {
  let count = 0;
  for (let i = 0; i < values.length; i++) {
    for (let j = i + 1; j < values.length; j++) {
      if ((values[i] ?? 0) > (values[j] ?? 0)) count += 1;
    }
  }
  return count;
}

/** Erstes benachbartes Paar, das verdreht ist. -1, wenn die Folge stimmt. */
function firstInversion(values: readonly number[]): number {
  for (let i = 0; i < values.length - 1; i++) {
    if ((values[i] ?? 0) > (values[i + 1] ?? 0)) return i;
  }
  return -1;
}

/** Lernrate aus der Konfidenz der Kette. Unsichere Ketten lernen schneller. */
export function learningRateFor(confidence: Confidence | null): number {
  return confidence ? LEARNING_RATE[confidence] : LEARNING_RATE.niedrig;
}
